// Route: /courses/new — мастер-бриф «собрать курс по теме» (COURSE-CREATION-FLOW §4.2).
//
// Полноэкранный роут, а не диалог: «одно поле за раз» в модалке тесно.
// До финала мастера НЕ создано ничего — уход отсюда не оставляет следа
// (инвариант §7.8), поэтому и подтверждения выхода здесь нет.
//
// `?topic=` — перенос темы с другого экрана: подборщик каталога (фаза 4) и
// мостик «соберите программу по теме» из пустого курса открывают бриф уже
// заполненным, чтобы человек не набирал тему второй раз.
//
// `?draft=` — ЧЕРНОВИК брифа (`brief-draft-storage.ts`). Вход без параметра
// заводит новый идентификатор и подставляет его в адрес (`replace`, без записи
// в историю): дальше перезагрузка, случайный «назад» и уснувший телефон
// возвращают человека на тот же шаг с теми же ответами. «Новый курс по теме»
// из меню ведёт на `/courses/new` без параметра — то есть открывает чистый
// мастер и НЕ затирает прежний черновик: ссылку на него можно скопировать и
// продолжить позже. §7.8 при этом цел: до финала брифа на сервере
// по-прежнему не создаётся ничего.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { cn, LIST_PAGE_STACK, NARROW_PAGE_SHELL, SectionHeader } from "@studyhub/ui";
import { IconSparkles } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { uuidv7 } from "uuidv7";
import { z } from "zod";
import { pruneCourseBriefDrafts } from "@/components/courses/create/brief/brief-draft-storage";
import { CourseBriefWizard } from "@/components/courses/create/brief/course-brief-wizard";
import { COURSE_BRIEF_TOPIC_MAX } from "@/components/courses/create/brief/use-course-brief-state";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";

const searchSchema = z.object({
	topic: z.string().max(COURSE_BRIEF_TOPIC_MAX).optional(),
	draft: z.string().min(8).max(64).optional(),
});

export const Route = createFileRoute("/courses/new")({
	validateSearch: tolerantSearch(searchSchema),
	component: CourseBriefPage,
	pendingComponent: PagePendingFallback,
});

function CourseBriefPage(): React.ReactElement {
	const { t } = useTranslation("courses");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: "/courses/new" });

	// Идентификатор заводится ОДИН раз на монтирование и сразу уходит в адрес.
	// `useState`-инициализатор, а не `useMemo`: при смене адреса (мы же его и
	// меняем) значение обязано остаться прежним, иначе черновик сменит ключ.
	const [draftId] = useState(() => search.draft ?? uuidv7());

	useEffect(() => {
		pruneCourseBriefDrafts();
	}, []);

	useEffect(() => {
		if (search.draft === draftId) return;
		void navigate({ search: (prev) => ({ ...prev, draft: draftId }), replace: true });
	}, [search.draft, draftId, navigate]);

	return (
		<div className={cn(NARROW_PAGE_SHELL, LIST_PAGE_STACK)}>
			<SectionHeader
				category="ai"
				icon={<IconSparkles stroke={1.75} />}
				title={t("create.brief.title")}
				description={t("create.brief.subtitle")}
			/>
			<CourseBriefWizard initialTopic={search.topic ?? ""} draftId={draftId} />
		</div>
	);
}
